import React from 'react';
import { ArrowRightIcon, FileTextIcon, ShoppingBagIcon, SproutIcon, StarIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../CtaButton';
import { SectionBadge } from '../SectionBadge';
import { HERO_FEATURES, HERO_IMAGE } from '../../data/content';
const FEATURE_ICONS = [FileTextIcon, ZapIcon, SproutIcon, ShoppingBagIcon];

/**
 * Above-the-fold content renders immediately (no scroll animation) so the
 * headline, mockup and CTA paint as fast as possible.
 */
export function Hero({
  onCta
}: {
  onCta: () => void;
}) {
  return <section className="relative w-full px-4 pb-12 pt-6 sm:px-5 sm:pb-16 sm:pt-12" data-id="element-102" data-mp="qtt1hd5jcg4q">
      <div className="relative mx-auto flex max-w-4xl flex-col items-center text-center" data-id="element-103" data-mp="vmvw7om1tvmi">
        <SectionBadge data-id="element-104" data-mp="10k44mxhealak">Para amantes del crochet y emprendedoras</SectionBadge>

        <h1 className="mt-5 max-w-3xl font-display text-[34px] leading-[1.08] text-cocoa min-[400px]:text-[40px] sm:text-6xl lg:text-7xl" data-id="element-105" data-mp="15lbbmubprgg6">
          Creá tu colección de <em className="italic text-terra" data-id="element-106" data-mp="1ah8qsxb8qgrd">bolsos de crochet</em> para este verano
        </h1>

        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-cocoa-soft sm:text-lg" data-id="element-107" data-mp="9lg90vc1oys6">
          Con <strong className="font-bold text-cocoa" data-id="element-108" data-mp="4r2jvanz7nvy">Megapack Bolsos Crochet Pro</strong> tenés todo lo que necesitás para
          aprender, crear y empezar a vender bolsos modernos paso a paso.
        </p>

        <div className="mt-8 w-full" data-id="element-109" data-mp="seaodqdkyswx">
          <div className="mx-auto max-w-3xl rounded-[20px] border border-sand-line bg-cream-card p-1.5 shadow-[0_18px_44px_rgba(58,41,32,0.14)] sm:rounded-[28px] sm:p-2.5" data-id="element-110" data-mp="sd936t2uoq1h">
            <img src={HERO_IMAGE} alt="Megapack Bolsos Crochet Pro: los 6 tomos y 5 bonos con bolsos de crochet, en tablet y celular" width={1448} height={1086} sizes="(min-width: 800px) 748px, calc(100vw - 44px)" loading="eager" fetchPriority="high" decoding="async" className="h-auto w-full rounded-[15px] sm:rounded-[22px]" data-id="element-111" data-mp="io8yct1o30fd" />
          </div>
        </div>

        <p className="mx-auto mt-7 max-w-xl text-sm leading-relaxed text-cocoa sm:text-base" data-id="element-112" data-mp="16cs697gt1b6x">
          <span className="block" data-id="element-113" data-mp="30mp9zn0j2co1">
            <strong className="font-bold" data-id="element-114" data-mp="35kqu8b9owi13">6 tomos completos</strong> +{' '}
            <strong className="font-bold" data-id="element-115" data-mp="35n36c7srny68">5 bonos exclusivos</strong>.
          </span>
          <span className="block" data-id="element-116" data-mp="2qvudlptkhwov">
            Aprendé técnicas, armá modelos atractivos y convertí tus tejidos en{' '}
            <span className="u-mark font-semibold" data-id="element-117" data-mp="2vsuj9zmi8xfg">productos listos para ofrecer</span>.
          </span>
          <span className="mt-1 block text-cocoa-dim" data-id="element-118" data-mp="2muiv04942bq6">Sin pasar horas buscando patrones distintos por internet.</span>
        </p>

        <div className="mt-7 w-full" data-id="element-119" data-mp="27y1llikhf769">
          <CtaButton className="w-full max-w-sm" onClick={onCta} data-id="element-120" data-mp="27z2xfp9bnxp1">
            Quiero mi Megapack <ArrowRightIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" data-id="element-121" data-mp="2cx4igfqb6735" />
          </CtaButton>
          <p className="mt-2.5 text-xs font-semibold text-cocoa-dim" data-id="element-122" data-mp="66eocz9zyg1c">
            Acceso inmediato · Pago seguro · <span className="text-cocoa" data-id="element-123" data-mp="b4gaxu6msxhk">Garantía de 7 días</span>
          </p>
        </div>

        <ul className="mx-auto mt-8 grid w-full max-w-sm grid-cols-2 gap-x-4 gap-y-3 sm:flex sm:max-w-2xl sm:flex-wrap sm:items-center sm:justify-center sm:gap-x-6 sm:gap-y-2.5" data-id="element-124" data-mp="1vnk02lj9h90c">
          {HERO_FEATURES.map((label, index) => {
          const Icon = FEATURE_ICONS[index % FEATURE_ICONS.length];
          return <li key={label} className="flex items-start gap-2 text-left text-[13px] font-semibold leading-snug text-cocoa sm:inline-flex sm:items-center sm:text-sm" data-id="element-125" data-mp="20kiqou3puxts" data-mpi={index}>
                <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-terra sm:mt-0" aria-hidden="true" data-id="element-126" data-mp="2aconch4wy3fb" />
                {label}
              </li>;
        })}
        </ul>

        <div className="mt-6 inline-flex max-w-full flex-col items-center justify-center gap-1.5 rounded-2xl bg-cream-deep px-5 py-3 text-center sm:flex-row sm:gap-3 sm:rounded-full sm:py-2.5" data-id="element-127" data-mp="2jclcejv7t4aw">
          <span className="flex gap-0.5" aria-label="Calificación 4.9 de 5" data-id="element-128" data-mp="oyk5dbpihazq">
            {Array.from({
            length: 5
          }).map((_, index) => <StarIcon key={index} className="h-3.5 w-3.5 fill-terra text-terra" aria-hidden="true" data-id="element-129" data-mp="qepn8k4zxqw8" data-mpi={index} />)}
          </span>
          <span className="text-xs font-bold text-cocoa" data-id="element-130" data-mp="vd9aqoz8naqp">
            4.9/5 · <span className="text-terra-deep" data-id="element-131" data-mp="10dnie5wxgio7">Elegido por amantes del crochet y emprendedoras</span>
          </span>
        </div>
      </div>
    </section>;
}